/// <reference types="vite/client" />
import { useEffect, useLayoutEffect, type ReactNode } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  redirect,
  useMatches,
  useRouter,
} from '@tanstack/react-router'
import appCss from '~/styles/app.css?url'
import { findQueryRedirect, findRedirect } from '~/redirects'
import { SiteHeader } from '~/components/SiteHeader'
import { SiteFooter } from '~/components/SiteFooter'
import { CookieBanner } from '~/components/CookieBanner'
import { installYoutubeFacades } from '~/lib/media/youtube'
import { installContentLinks } from '~/lib/content/links'
import { installOverscrollColor } from '~/lib/browser/overscroll'
import { SPA_SHELL_ATTRIBUTE, shellWasRendered } from '~/lib/site/spa-shell'
import { restoreTheme, themeScript } from '~/lib/browser/theme'
import { OG_IMAGE, SITE_NAME, absoluteUrl } from '~/lib/site/seo'

const DESCRIPTION =
  'DxE is a global grassroots network of animal rights activists. Together, we will achieve social and political change for animals in one generation.'
const TITLE = 'Direct Action Everywhere - Until every animal is free'

const GTM_ID = 'GTM-MHKSWMJ'
const META_PIXEL_ID = '899259337223849'

// Analytics: the queues (dataLayer, fbq) are set up immediately so anything
// pushed early is kept, but the third-party scripts are only fetched once the
// page has loaded and the browser is idle (or after a 4s cap).
// Page views on client-side navigation: GA4 sends them itself (enhanced
// measurement, history changes). So does the Meta pixel, but it sent one
// extra per navigation so far (1, 2, 3, ... on the 1st, 2nd, 3rd page);
// disablePushState leaves exactly one per navigation.
const analyticsScript = `(function(w,d){
w.dataLayer=w.dataLayer||[];
w.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'});
var n=w.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!w._fbq)w._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];n.disablePushState=!0;
n('init','${META_PIXEL_ID}');n('track','PageView');
function load(src){var s=d.createElement('script');s.async=true;s.src=src;d.head.appendChild(s)}
var started=false;
function start(){if(started)return;started=true;
load('https://www.googletagmanager.com/gtm.js?id=${GTM_ID}');
load('https://connect.facebook.net/en_US/fbevents.js')}
function schedule(){
if(w.requestIdleCallback)w.requestIdleCallback(start,{timeout:4000});else setTimeout(start,2000)}
if(d.readyState==='complete')schedule();else w.addEventListener('load',function(){setTimeout(schedule,500)})
})(window,document)`

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
}>()({
  beforeLoad: ({ location }) => {
    const to = findRedirect(location.pathname)
    if (to) throw redirect({ href: to, statusCode: 301 })
    // Old query-string URLs (/news?type=blog&page=2 -> /news/blog?page=2,
    // /whistleblower-support?language=spanish); the CDN does this too.
    const queryTo = findQueryRedirect(location.pathname, (k) => {
      // TanStack parses numeric params to numbers.
      const v = (location.search as Record<string, unknown>)[k]
      return typeof v === 'string' || typeof v === 'number'
        ? String(v)
        : undefined
    })
    if (queryTo) throw redirect({ href: queryTo, statusCode: 301 })
  },
  head: ({ matches }) => {
    // Only the root matched: an unknown path (the CDN's SPA shell), which has
    // no URL of its own to declare.
    const matched = matches.length > 1
    return {
      meta: [
        { charSet: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        // (No theme-color here: the theme script owns that meta, because React
        // would add a second one next to a script-changed copy; theme.ts.)
        // Set for PR previews and CI builds (preview.yml, ci.yml).
        ...(import.meta.env.VITE_NOINDEX === 'true'
          ? [{ name: 'robots', content: 'noindex, nofollow' }]
          : []),
        { title: TITLE },
        { name: 'description', content: DESCRIPTION },
        { property: 'og:type', content: 'website' },
        { property: 'og:site_name', content: SITE_NAME },
        ...(matched
          ? [
              {
                property: 'og:url',
                content: absoluteUrl(matches[matches.length - 1].pathname),
              },
            ]
          : []),
        { property: 'og:title', content: TITLE },
        { property: 'og:description', content: DESCRIPTION },
        { property: 'og:image', content: OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
      ],
      links: [
        ...(matched
          ? [
              {
                rel: 'canonical',
                href: absoluteUrl(matches[matches.length - 1].pathname),
              },
            ]
          : []),
        // Self-hosted Inter variable font (public/fonts), used above the fold.
        {
          rel: 'preload',
          as: 'font',
          type: 'font/woff2',
          href: '/fonts/inter-latin-var.woff2',
          crossOrigin: 'anonymous' as const,
        },
        { rel: 'stylesheet', href: appCss },
        { rel: 'icon', type: 'image/png', href: '/images/favicon.png' },
        { rel: 'apple-touch-icon', href: '/images/apple-touch-icon.png' },
      ],
      scripts: [{ children: analyticsScript }],
    }
  },
  component: RootComponent,
})

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    /** Show only the logo in the header, without the nav. */
    minimalHeader?: boolean
  }
}

function RootComponent() {
  // A rendered (not hydrated) SPA shell has rebuilt <html> and <head>, which
  // loses the theme the head script applied. Put it back before paint.
  useLayoutEffect(() => {
    if (shellWasRendered()) restoreTheme()
  }, [])
  useEffect(installYoutubeFacades, [])
  const router = useRouter()
  useEffect(() => installContentLinks(router), [router])
  useEffect(installOverscrollColor, [])
  const minimalHeader = useMatches({
    select: (matches) => matches.some((m) => m.staticData.minimalHeader),
  })
  return (
    <RootDocument>
      <SkipLink />
      {/* Column layout so the footer reaches the window bottom on short pages.
          Paints the page so html/body can carry Safari's bar and overscroll
          colour (src/lib/browser/overscroll.ts). */}
      <div className="flex min-h-screen flex-col bg-surface">
        <SiteHeader minimal={minimalHeader} />
        {/* Landmark for every page (routes render fragments); focusable so
            the skip link can move focus to it. */}
        <main id="main" tabIndex={-1} className="flex-1 outline-none">
          <Outlet />
        </main>
        <SiteFooter />
      </div>
      <CookieBanner />
    </RootDocument>
  )
}

function SkipLink() {
  return (
    <a
      href="#main"
      className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[10000] focus:rounded-sm focus:bg-surface focus:px-4 focus:py-2 focus:font-bold focus:text-accent focus:shadow-lg"
    >
      Skip to main content
    </a>
  )
}

function RootDocument({ children }: { children: ReactNode }) {
  // True only while prerendering /_shell.html (the client router never is), so
  // the attribute is in the shell's HTML alone; see src/client.tsx.
  // Not in dev, where every page is a shell prerendered for its own URL, so
  // it hydrates cleanly and keeps React's mismatch checks.
  const isShell = useRouter().isShell() && !import.meta.env.DEV
  return (
    // The theme script adds .dark to <html> before hydration.
    <html
      lang="en"
      suppressHydrationWarning
      {...(isShell ? { [SPA_SHELL_ATTRIBUTE]: '' } : {})}
    >
      <head>
        {/* First, so the theme applies before anything paints, and so route
            head scripts come after it: a script that only one side renders
            (the home page's JSON-LD in a dev SPA shell, which hydrates before
            the route loads) is then a trailing extra tag React skips, instead
            of shifting which <script> this one is compared with. */}
        <script dangerouslySetInnerHTML={{ __html: themeScript }} />
        <HeadContent />
      </head>
      <body className="font-sans text-sm leading-5 text-fg-body">
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
            title="Google Tag Manager"
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  )
}
